export type ObserverCallback = (isVisible: boolean) => void;

type Intersection = (
  elem: HTMLElement,
  cb: ObserverCallback,
  options?: IntersectionObserverInit,
  rootId?: string,
  once?: boolean
) => Instance;

export interface Instance {
  id: string;
  element: HTMLElement;
  callback: ObserverCallback;
  options: IntersectionObserverInit;
  observer: IntersectionObserver;
  unobserve: () => void;
  once: boolean;
}

const buildId = ({ rootMargin, threshold }: IntersectionObserverInit) =>
  rootMargin
    ? `${(threshold || 0).toString()}_${rootMargin.replace(/ /g, '').toString()}`
    : `${(threshold || 0).toString()}`;

export const observers: Map<string, IntersectionObserver> = new Map<string, IntersectionObserver>();
export const instances: Map<HTMLElement, Instance> = new Map<HTMLElement, Instance>();

const onChange: IntersectionObserverCallback = entries =>
  entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
    const instance = instances.get(target as HTMLElement);
    if (!instance) {
      return;
    }
    const {
      options: { threshold },
      callback,
      once
    } = instance;

    const visCheck = (threshold: number) => intersectionRatio >= threshold;
    let vis = Array.isArray(threshold)
      ? threshold.some(visCheck)
      : threshold !== undefined
      ? visCheck(threshold)
      : false;

    vis = isIntersecting !== undefined ? vis && isIntersecting : vis;
    if (vis && once) {
      instance.unobserve();
    }
    if (callback) {
      callback(vis);
    }
  });

const unobserve: (elem: HTMLElement) => () => void = (elem: HTMLElement) => () => {
  const instance = instances.get(elem);
  if (!instance) {
    return;
  }

  const observer = observers.get(instance.id) || instance.observer;
  observer.unobserve(elem);

  let active = false;
  instances.forEach(({ id }, element) => {
    if (id === instance.id && element !== elem) {
      active = true;
    }
  });

  if (!active) {
    observer.disconnect();
    observers.delete(instance.id);
  }

  instances.delete(elem);
};

export const observer: Intersection = (
  elem,
  cb,
  options = { threshold: 0 },
  rootId,
  once = true
) => {
  const id = rootId ? `${rootId}_${buildId(options)}` : buildId(options);

  let observer = observers.get(id);
  if (!observer) {
    observer = new IntersectionObserver(onChange, options);
    observers.set(id, observer);
  }
  observer.observe(elem);
  const instance: Instance = {
    id,
    callback: cb,
    element: elem,
    observer,
    options,
    unobserve: unobserve(elem),
    once
  };
  instances.set(elem, instance);

  return instance;
};
